import React from 'react'
import { format as formatDate } from 'date-fns'
import { Heading, Text, Link, HStack, Box, Stack } from '@chakra-ui/react'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid'
import { verifications } from '@homevest/utils'
import { UserIdentityVerification } from '@homevest/types/user-identity-verifications'

import { mapContentToBadge } from 'components/TailwindUIToolkit/badges'

const { VERIFICATION_STATUSES } = verifications

const UserIdentityVerificationInfo: React.FC<
  React.PropsWithChildren<{
    userIdentityVerification: UserIdentityVerification | null
  }>
> = ({ userIdentityVerification }) => {
  if (!userIdentityVerification) {
    return (
      <HStack spacing={5}>
        <Text>This user has not yet attempted to verify their identity!</Text>
      </HStack>
    )
  }

  const completedAt = userIdentityVerification.completed_at

  const isFailed = userIdentityVerification.status === VERIFICATION_STATUSES.FAILED
  const isInvalidated = !!userIdentityVerification.invalidated_at
  const invalidatedAt = userIdentityVerification.invalidated_at

  return (
    <Stack>
      <Heading>
        <Link
          href={`https://dashboard.stripe.com/identity/verification-sessions/${userIdentityVerification.stripe_session_id}`}
          isExternal
        >
          <HStack>
            <Heading size='md' marginBottom={3} as='h3'>
              Identity Verification
            </Heading>
            <Box mb={4}>
              <ArrowTopRightOnSquareIcon color='teal' className='h-4 w-4' />
            </Box>
          </HStack>
        </Link>
      </Heading>
      <div className='flex flex-row justify-between gap-3'></div>
      <div className='flex flex-col gap-2'>
        <HStack fontSize='md'>
          <Text as='span' fontWeight={'semibold'}>
            Status:
          </Text>{' '}
          {mapContentToBadge(userIdentityVerification.status, { strikethrough: isInvalidated })}
          {isInvalidated && (
            <Text as='i'>(Invalidated on {formatDate(new Date(invalidatedAt!), 'M/d/yy')})</Text>
          )}
        </HStack>
        <Text fontSize='md'>
          <Text as='span' fontWeight={'semibold'}>
            Completed At:
          </Text>{' '}
          {completedAt ? formatDate(new Date(completedAt), 'M/d/yy h:mm a') : '-'}
        </Text>
      </div>

      {isFailed && (
        <Box marginY={3}>
          <Text>
            <Text as='b'>Failure Message: </Text>
            {userIdentityVerification.failure_message ?? 'Unknown failure reason'}
          </Text>
        </Box>
      )}
    </Stack>
  )
}

const UserIdentityVerificationInfoWrapper: React.FC<
  React.PropsWithChildren<{
    userIdentityVerification: UserIdentityVerification | null
  }>
> = ({ userIdentityVerification }) => {
  return (
    <>
      <UserIdentityVerificationInfo userIdentityVerification={userIdentityVerification} />
    </>
  )
}

export default UserIdentityVerificationInfoWrapper
